import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Course, CourseEnrollment, EnrollmentStatus, Group, Login, Tag, UserGroup } from '../../apis/api-client';
import { certificatesClient, coursesClient, groupsClient, reportsClient, tagsClient, usersClient } from '../../apis/client-instances';
import { alert } from '../../hooks/function';
import { useEffectiveWorkspaceId } from '../../hooks/useEffectiveWorkspaceId';
import { usePagedList } from '../../hooks/usePagedList';
import { useUrlFilters } from '../../hooks/useUrlFilters';
import PagedTable, { PagedTableColumn } from '../common/PagedTable';
import UserAutocomplete from '../common/UserAutocomplete';
import { CourseAutocomplete, EmptyState, EntityLink, TagAutocomplete } from '../common';
import { CertificateIcon, DownloadIcon, WorkspacesIcon } from '../icons';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '../ui';
import { activityStatusLabel, activityStatusPalette } from '../../utils/reportingHelpers';

type SortKey = 'student' | 'course' | 'status' | 'started' | 'completed';

export default function ActivityReport() {
  const { enqueueSnackbar } = useSnackbar();
  const effectiveWorkspaceId = useEffectiveWorkspaceId();
  const location = useLocation();

  // URL-backed filters so deep links and "Back to Activity Report" restore state.
  const { getInt, getIntArray, getString, getBool, setParams } = useUrlFilters();
  const filterGroupId = getInt('groupId');
  const courseIds = getIntArray('courseIds');
  const userIds = getIntArray('userIds');
  const filterStatus = getInt('status');
  const tagIds = getIntArray('tagIds');
  const filterDateFrom = getString('from');
  const filterDateTo = getString('to');
  const sortKey = (getString('sort', 'student') as SortKey);
  const sortDescending = getBool('dir', false);
  const initialQuery = getString('q');

  // Object-typed filters are stored in URL as IDs; we keep the hydrated Course/Tag/Login
  // objects in local state for the autocomplete chips.
  const [filterCourses, setFilterCourses] = useState<Course[]>([]);
  const [filterPersons, setFilterPersons] = useState<Login[]>([]);
  const [filterTags, setFilterTags] = useState<Tag[]>([]);

  // Filter option sources (loaded once per workspace).
  const [groupMemberships, setGroupMemberships] = useState<UserGroup[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);

  // Cert / download state
  const [certLoadingIds, setCertLoadingIds] = useState<Set<number>>(new Set());
  const [downloading, setDownloading] = useState(false);

  const dateFrom = filterDateFrom ? new Date(filterDateFrom).toISOString() : undefined;
  const dateTo = filterDateTo ? new Date(filterDateTo).toISOString() : undefined;

  const paged = usePagedList<CourseEnrollment>({
    fetcher: useCallback(
      async ({ page, pageSize, search }) => {
        if (!effectiveWorkspaceId) {
          return { items: [], totalCount: 0, page, pageSize };
        }
        const result = await reportsClient.searchActivity(
          effectiveWorkspaceId,
          search || undefined,
          courseIds.length > 0 ? courseIds : undefined,
          userIds.length > 0 ? userIds : undefined,
          filterStatus,
          tagIds.length > 0 ? tagIds : undefined,
          filterGroupId,
          dateFrom as any,
          dateTo as any,
          sortKey,
          sortDescending,
          page,
          pageSize
        );
        return {
          items: result.items ?? [],
          totalCount: result.totalCount ?? 0,
          page: result.page ?? page,
          pageSize: result.pageSize ?? pageSize,
        };
      },
      [effectiveWorkspaceId, courseIds, userIds, filterStatus, tagIds, filterGroupId, dateFrom, dateTo, sortKey, sortDescending]
    ),
    deps: [
      effectiveWorkspaceId ?? null,
      courseIds.join(','),
      userIds.join(','),
      filterStatus ?? '',
      tagIds.join(','),
      filterGroupId ?? '',
      dateFrom ?? '',
      dateTo ?? '',
      sortKey,
      sortDescending,
    ],
    initialSearch: initialQuery,
    onSearchChange: (s) => setParams({ q: s || null }),
    onError: () => alert(enqueueSnackbar, 'Failed to load activity', 'error'),
  });

  // Load filter options once per workspace
  useEffect(() => {
    if (!effectiveWorkspaceId) {
      setGroupMemberships([]);
      setGroups([]);
      return;
    }
    (async () => {
      try {
        const [memberships, grpResult] = await Promise.all([
          groupsClient.getAllGroupMemberships(effectiveWorkspaceId),
          groupsClient.searchGroups(effectiveWorkspaceId, undefined, 1, 200, undefined),
        ]);
        setGroupMemberships(memberships);
        setGroups(grpResult.items ?? []);
      } catch {
        // Filter option failures are non-fatal — table still works.
      }
    })();
  }, [effectiveWorkspaceId]);

  // Hydrate Course chips from courseIds in URL.
  // We insert placeholder { id, title: "Course #N" } chips immediately so the UI
  // is responsive, then upgrade to real Course objects as fetches return.
  useEffect(() => {
    const have = new Set(filterCourses.map(c => c.id!).filter(Boolean));
    const want = new Set(courseIds);
    if (have.size === want.size && Array.from(have).every(id => want.has(id))) return;

    const placeholders: Course[] = courseIds.map(id => {
      const existing = filterCourses.find(c => c.id === id);
      return existing ?? ({ id, title: `Course #${id}` } as Course);
    });
    setFilterCourses(placeholders);

    const missing = courseIds.filter(id => !filterCourses.some(c => c.id === id));
    missing.forEach(id => {
      coursesClient.getCourseById(id).then(c => {
        if (c) setFilterCourses(prev => prev.map(p => p.id === id ? c : p));
      }).catch(() => { /* placeholder chip is good enough */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseIds.join(',')]);

  // Hydrate Person chips from userIds in URL.
  useEffect(() => {
    if (!effectiveWorkspaceId) return;
    const have = new Set(filterPersons.map(p => p.id!).filter(Boolean));
    const want = new Set(userIds);
    if (have.size === want.size && Array.from(have).every(id => want.has(id))) return;

    setFilterPersons(userIds.map(id => filterPersons.find(p => p.id === id) ?? ({ id } as Login)));
    if (userIds.length > 0) {
      usersClient.searchUsersPaged(undefined, undefined, undefined, undefined, 1, userIds.length, userIds)
        .then(res => {
          const fetched = res.items ?? [];
          setFilterPersons(prev => prev.map(p => fetched.find(f => f.id === p.id) ?? p));
        }).catch(() => { /* placeholder chips are good enough */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIds.join(','), effectiveWorkspaceId]);

  // Hydrate Tag chips from tagIds in URL.
  useEffect(() => {
    const have = new Set(filterTags.map(t => t.id!).filter(Boolean));
    const want = new Set(tagIds);
    if (have.size === want.size && Array.from(have).every(id => want.has(id))) return;

    const placeholders: Tag[] = tagIds.map(id => {
      const existing = filterTags.find(t => t.id === id);
      return existing ?? ({ id, name: `Tag #${id}` } as Tag);
    });
    setFilterTags(placeholders);

    if (tagIds.length > 0) {
      tagsClient.searchTags(undefined, undefined, undefined, false, false, tagIds, 1, tagIds.length).then(res => {
        const fetched = res.items ?? [];
        setFilterTags(prev => prev.map(p => fetched.find(t => t.id === p.id) ?? p));
      }).catch(() => { /* placeholder chips are good enough */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagIds.join(',')]);

  // userId → groupNames map for the per-row Groups column.
  const userGroupMap = useMemo(() => {
    const map = new Map<number, string[]>();
    for (const m of groupMemberships) {
      if (!m.userId || !m.groupName) continue;
      const list = map.get(m.userId) ?? [];
      list.push(m.groupName);
      map.set(m.userId, list);
    }
    return map;
  }, [groupMemberships]);

  // Workspace switch: clear filters that reference workspace-scoped IDs
  // (group / student / course). Tags + status + dates carry across.
  const prevWsIdRef = useRef(effectiveWorkspaceId);
  useEffect(() => {
    if (prevWsIdRef.current != null && prevWsIdRef.current !== effectiveWorkspaceId) {
      setParams({ groupId: null, userIds: null, courseIds: null });
    }
    prevWsIdRef.current = effectiveWorkspaceId;
  }, [effectiveWorkspaceId, setParams]);

  const hasAnyFilter =
    courseIds.length > 0 || userIds.length > 0 || tagIds.length > 0 ||
    filterStatus !== undefined || filterGroupId !== undefined ||
    !!filterDateFrom || !!filterDateTo;

  const clearAllFilters = () => {
    setParams({
      courseIds: null, userIds: null, tagIds: null,
      status: null, groupId: null, from: null, to: null,
    });
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setParams({ dir: !sortDescending });
    else setParams({ sort: key, dir: false });
  };

  const sortLabel = (key: SortKey, label: string) =>
    `${label}${sortKey === key ? (sortDescending ? ' ↓' : ' ↑') : ''}`;

  const handleDownloadCsv = async () => {
    if (!effectiveWorkspaceId) return;
    setDownloading(true);
    try {
      // Build the same query string the server endpoint expects.
      const params = new URLSearchParams();
      params.set('workspaceId', String(effectiveWorkspaceId));
      if (paged.search) params.set('search', paged.search);
      for (const id of courseIds) params.append('courseIds', String(id));
      for (const id of userIds) params.append('userIds', String(id));
      if (filterStatus !== undefined) params.set('enrollmentStatus', String(filterStatus));
      for (const id of tagIds) params.append('tagIds', String(id));
      if (filterGroupId !== undefined) params.set('groupId', String(filterGroupId));
      if (dateFrom) params.set('startDate', dateFrom);
      if (dateTo) params.set('endDate', dateTo);
      params.set('sortKey', sortKey);
      params.set('sortDescending', String(sortDescending));

      const base = process.env.REACT_APP_API_BASE_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${base}/api/Reports/activity/export/csv?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error('CSV download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert(enqueueSnackbar, 'Failed to download CSV', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCert = async (certificateId: number, loadingKey: number) => {
    setCertLoadingIds(prev => new Set(prev).add(loadingKey));
    try {
      const url = await certificatesClient.getCertificateDownloadUrl(certificateId);
      window.open(url, '_blank');
    } catch {
      alert(enqueueSnackbar, 'Failed to load certificate', 'error');
    } finally {
      setCertLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(loadingKey);
        return next;
      });
    }
  };

  // Capture the current URL so child entity-links can return to this exact tab+filter view.
  const fromUrl = `${location.pathname}${location.search}`;
  const entityState = { from: fromUrl, fromText: 'Back to Activity Report' };

  const columns: PagedTableColumn<CourseEnrollment>[] = [
    {
      key: 'student',
      label: (
        <Box component="span" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('student')}>
          {sortLabel('student', 'Student')}
        </Box>
      ),
      render: (row) => (
        <EntityLink
          to={row.userId != null ? `/students/${row.userId}` : null}
          tooltip="Open student profile"
          state={entityState}
        >
          <Typography variant="body2" fontWeight={500}>
            {row.lastName}, {row.firstName}
          </Typography>
          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
        </EntityLink>
      ),
    },
    {
      key: 'groups',
      label: 'Groups',
      render: (row) => {
        const gs = row.userId ? userGroupMap.get(row.userId) ?? [] : [];
        return gs.length > 0
          ? <Stack direction="row" spacing={0.5} flexWrap="wrap">{gs.map(g => <Chip key={g} label={g} size="small" variant="outlined" />)}</Stack>
          : <Typography variant="caption" color="text.disabled">—</Typography>;
      },
    },
    {
      key: 'course',
      label: (
        <Box component="span" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('course')}>
          {sortLabel('course', 'Course')}
        </Box>
      ),
      render: (row) => (
        <EntityLink
          to={row.courseId != null ? `/course/${row.courseId}` : null}
          tooltip="Open course"
          state={entityState}
        >
          <span>{row.courseTitle}</span>
        </EntityLink>
      ),
    },
    {
      key: 'status',
      label: (
        <Box component="span" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>
          {sortLabel('status', 'Status')}
        </Box>
      ),
      render: (row) => {
        const palette = activityStatusPalette(row.enrollmentStatus ?? null);
        return (
          <Chip
            label={activityStatusLabel(row.enrollmentStatus ?? null)}
            size="small"
            sx={{
              bgcolor: palette.bgcolor,
              color: palette.color,
              fontWeight: 600,
              height: 22,
              fontSize: '0.72rem',
              '& .MuiChip-label': { px: 1 },
            }}
          />
        );
      },
    },
    {
      key: 'started',
      label: (
        <Box component="span" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('started')}>
          {sortLabel('started', 'Started')}
        </Box>
      ),
      render: (row) => row.startedAt ? new Date(row.startedAt).toLocaleDateString() : '—',
    },
    {
      key: 'completed',
      label: (
        <Box component="span" sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('completed')}>
          {sortLabel('completed', 'Completed')}
        </Box>
      ),
      render: (row) => row.completedAt ? new Date(row.completedAt).toLocaleDateString() : '—',
    },
    {
      key: 'progress',
      label: 'Progress',
      render: (row) => {
        const isCompleted = row.enrollmentStatus === EnrollmentStatus.Completed;
        return row.progressPercentage != null && row.progressPercentage > 0
          ? `${Math.round(row.progressPercentage)}%`
          : isCompleted ? '100%' : '—';
      },
    },
    {
      key: 'cert',
      label: 'Cert',
      align: 'center',
      render: (row) => {
        if (row.certificateId == null) {
          return <Typography variant="caption" color="text.disabled">—</Typography>;
        }
        const inFlight = certLoadingIds.has(row.certificateId);
        return (
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={inFlight ? <CircularProgress size={14} color="inherit" /> : <CertificateIcon />}
            onClick={() => handleDownloadCert(row.certificateId!, row.certificateId!)}
            disabled={inFlight}
          >
            {inFlight ? '…' : 'Cert'}
          </Button>
        );
      },
    },
  ];

  if (!effectiveWorkspaceId) {
    return (
      <EmptyState
        icon={<WorkspacesIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
        title="Select a workspace to view this report"
        subtitle="Use the workspace switcher in the sidebar to choose a workspace"
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3, alignItems: 'center' }}>
        <Autocomplete
          options={groups}
          getOptionLabel={g => g.name ?? ''}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={filterGroupId === undefined ? null : groups.find(g => g.id === filterGroupId) ?? null}
          onChange={(_, v) => setParams({ groupId: v?.id ?? null })}
          size="small"
          sx={{ minWidth: 220 }}
          renderInput={params => <TextField {...params} label="Group" placeholder="All Groups" />}
        />

        <UserAutocomplete
          multiple
          value={filterPersons}
          onChange={(v) => {
            const next = (v as Login[]) ?? [];
            setFilterPersons(next);
            setParams({ userIds: next.map(p => p.id!).filter(Boolean) });
          }}
          workspaceId={effectiveWorkspaceId}
          label="Student"
          sx={{ minWidth: 220 }}
        />

        <CourseAutocomplete
          multiple
          value={filterCourses}
          onChange={(v) => {
            const next = (v as Course[]) ?? [];
            setFilterCourses(next);
            setParams({ courseIds: next.map(c => c.id!).filter(Boolean) });
          }}
          label="Course"
        />

        <Select
          value={filterStatus ?? ''}
          onChange={e => {
            // 0 is never a valid enrollment-status value, so falsy = "all".
            const raw = e.target.value as number | '';
            setParams({ status: raw ? Number(raw) : null });
          }}
          displayEmpty
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value={EnrollmentStatus.NotStarted}>Not Started</MenuItem>
          <MenuItem value={EnrollmentStatus.InProgress}>In Progress</MenuItem>
          <MenuItem value={EnrollmentStatus.Completed}>Completed</MenuItem>
        </Select>

        <TagAutocomplete
          multiple
          value={filterTags}
          onChange={(v) => {
            const next = (v as Tag[]) ?? [];
            setFilterTags(next);
            setParams({ tagIds: next.map(t => t.id!).filter(Boolean) });
          }}
          workspaceId={effectiveWorkspaceId ?? undefined}
          label="Tag"
          sx={{ minWidth: 200 }}
        />

        <TextField
          label="From"
          type="date"
          size="small"
          value={filterDateFrom}
          onChange={e => setParams({ from: e.target.value || null })}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={filterDateTo}
          onChange={e => setParams({ to: e.target.value || null })}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />

        {hasAnyFilter && (
          <Button size="small" variant="text" color="inherit" onClick={clearAllFilters}>
            Clear filters
          </Button>
        )}

        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadCsv}
          disabled={downloading}
        >
          {downloading ? 'Preparing…' : 'CSV'}
        </Button>
      </Stack>

      <PagedTable<CourseEnrollment>
        columns={columns}
        rows={paged.items}
        totalCount={paged.totalCount}
        page={paged.page}
        pageSize={paged.pageSize}
        loading={paged.loading}
        rowKey={(r) => `${r.userId}-${r.courseId}-${r.enrollmentId ?? 'none'}`}
        onPageChange={paged.setPage}
        onPageSizeChange={paged.setPageSize}
        search={paged.search}
        searchPlaceholder="Search name, email, course…"
        onSearchChange={paged.setSearch}
        emptyMessage="No enrollment data matches the current filters"
      />
    </Box>
  );
}
